import { event } from '../../contracts/events.js'
import { AppError, serializeError } from '../../contracts/errors.js'

export class GenerationScheduler {
  constructor({ concurrency = 4, now = () => Date.now() } = {}) {
    this.concurrency = Math.max(1, Number(concurrency) || 1)
    this.now = now
    this.active = 0
  }

  async run(jobs, { execute, signal, onEvent } = {}) {
    if (typeof execute !== 'function') throw new AppError('generation.executor-missing', '缺少生成执行器', { category: 'configuration' })
    const list = Array.isArray(jobs) ? jobs : []
    const results = []
    let cursor = 0
    let completed = 0
    const emit = (type, payload) => onEvent?.(event(type, payload))
    emit('run.created', { total: list.length })
    const worker = async () => {
      while (true) {
        const index = cursor++
        if (index >= list.length) return
        const job = list[index]
        if (signal?.aborted) {
          results[index] = { status: 'cancelled', job }
          emit('job.cancelled', { job, index })
          continue
        }
        emit('job.queued', { job, index })
        emit('job.started', { job, index })
        try {
          const value = await execute(job, { signal, index })
          results[index] = { status: 'succeeded', job, value }
          emit('job.succeeded', { job, index, value })
        } catch (error) {
          const serialized = serializeError(error)
          results[index] = { status: signal?.aborted ? 'cancelled' : 'failed', job, error: serialized }
          emit(results[index].status === 'cancelled' ? 'job.cancelled' : 'job.failed', { job, index, error: serialized })
        } finally {
          completed += 1
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(this.concurrency, list.length) }, worker))
    const cancelled = results.filter((result) => result?.status === 'cancelled').length
    const failed = results.filter((result) => result?.status === 'failed').length
    const succeeded = results.filter((result) => result?.status === 'succeeded').length
    const status = signal?.aborted || cancelled ? 'cancelled' : failed ? 'failed' : 'completed'
    emit(status === 'completed' ? 'run.completed' : status === 'cancelled' ? 'run.cancelled' : 'run.failed', { total: list.length, completed, succeeded, failed, cancelled })
    return { status, total: list.length, completed, succeeded, failed, cancelled, results }
  }
}
